// Pigment settling onto the paper and lifting back into the water.
//
// Suspended particles settle at a rate that grows with particle density and
// as the water film thins (particles have less depth to fall through).
// Granulating pigments (coarse, dense particles such as ultramarine) settle
// preferentially into paper valleys. Settled pigment lifts back into water at
// a rate reduced by the pigment's staining strength and by how firmly the
// binder has set; gum arabic re-dissolves (rewet ~1) while acrylic does not.
// When a cell's water is gone, all remaining suspended pigment is deposited
// where it is: combined with the outward flow to a pinned, faster-evaporating
// edge this produces edge darkening.

import { PIGMENT_SLOTS } from '../core/PaintState.js';
import { tileBounds } from './region.js';
import { WATER_WET_DEPTH, PAPER_WET_SATURATION } from './WaterTransport.js';

export const DEFAULT_PIGMENT_PARAMS = Object.freeze({
  settling: 0.12, // per second, times pigment density, in a thin film
  granulation: 1.0, // global granulation strength (surface control)
  lifting: 0.25, // per second for a non-staining pigment on unset binder
  binderSetting: 0.25, // per second while dry
  binderRedissolve: 0.6, // per second while wet, times the cell's rewet factor
});

export function stepPigmentExchange(state, region, dt, env = {}) {
  const p = { ...DEFAULT_PIGMENT_PARAMS, ...(env.pigment || {}) };
  const { width, water, paperSat, binderSet, depRewet } = state;
  const relief = state.surface.heightField;
  // Surface-dependent strength of valley settling (granulation control).
  const surfaceGranulation = state.surface.preset.granulation ?? 1;
  const settleBase = [];
  for (let k = 0; k < PIGMENT_SLOTS; k += 1) {
    const pigment = state.slots[k];
    settleBase.push(pigment ? pigment : null);
  }
  for (const t of region.tiles) {
    const [x0, y0, x1, y1] = tileBounds(state, t);
    for (let y = y0; y < y1; y += 1) {
      for (let x = x0; x < x1; x += 1) {
        const i = y * width + x;
        const h = water[i];
        const wet = h > WATER_WET_DEPTH;
        const damp = wet || paperSat[i] > PAPER_WET_SATURATION;
        // Binder: sets while dry, partly re-dissolves when re-wetted (if the medium allows).
        if (!damp) binderSet[i] += (1 - binderSet[i]) * (1 - Math.exp(-p.binderSetting * dt));
        else if (wet) binderSet[i] -= binderSet[i] * depRewet[i] * (1 - Math.exp(-p.binderRedissolve * dt));
        const valley = 1 - relief[i];
        // Particles mostly stay suspended until the film is thin: settling
        // grows steeply as the depth falls (they have less far to fall and
        // less flow to keep them moving).
        const t1 = 1 / (1 + h / 0.08);
        const thin = 0.05 + 0.95 * t1 * t1 * t1;
        for (let k = 0; k < PIGMENT_SLOTS; k += 1) {
          const pigment = settleBase[k];
          if (!pigment) continue;
          const g = state.wetPig[k];
          const d = state.depPig[k];
          if (g[i] > 0) {
            if (!wet) {
              d[i] += g[i];
              g[i] = 0;
            } else {
              const granular = Math.max(0, 1 + 2.2 * pigment.granulation * p.granulation * surfaceGranulation * (valley - 0.5));
              const rate = p.settling * pigment.density * thin * granular;
              const settled = g[i] * (1 - Math.exp(-rate * dt));
              g[i] -= settled;
              d[i] += settled;
            }
          }
          if (wet && d[i] > 0) {
            const looseness = (1 - pigment.staining) * (1 - binderSet[i]);
            const lifted = d[i] * (1 - Math.exp(-p.lifting * looseness * (h / (h + 0.05)) * dt));
            d[i] -= lifted;
            g[i] += lifted;
          }
        }
      }
    }
  }
}
